/**
 * PaymentHistoryPage.js
 * Standalone page listing all the user's magazine payment history.
 * Covers both yearly subscriptions and per-copy orders, sorted by date descending.
 */
import React, { useState, useEffect } from 'react';
import { History, Loader, RefreshCw } from 'lucide-react';
import { vendorService } from '../../services/api';

const PaymentHistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await vendorService.getMagazinePaymentHistory();
      if (res.success && res.data?.history) {
        setHistory(res.data.history);
      } else {
        setError(res.error || 'Failed to load payment history.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold">Completed</span>;
      case 'PENDING':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-xs font-semibold">Pending</span>;
      case 'FAILED':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 text-xs font-semibold">Failed</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 text-xs font-semibold">{status}</span>;
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-5 sm:mb-6 text-left">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          History of Payments
        </h1>
        <p className="text-gray-500 text-xs sm:text-sm mt-1 font-medium">All your magazine subscription and order payments</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader size={28} className="animate-spin text-[#2ab391]" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-red-700 text-xs sm:text-sm">
          {error}
        </div>
      ) : history.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-200/90 shadow-xs text-center p-6">
          <History size={36} className="text-gray-300 mb-3" />
          <p className="text-gray-800 font-bold text-base">No payment history yet</p>
          <p className="text-gray-400 text-xs mt-1">Your magazine payments will appear here after purchase.</p>
        </div>
      ) : (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-4 mb-5 sm:mb-6">
            {[
              { label: 'Total Transactions', value: history.length },
              { label: 'Completed', value: history.filter(h => h.status === 'COMPLETED').length, color: 'text-emerald-600' },
              { label: 'Pending', value: history.filter(h => h.status === 'PENDING').length, color: 'text-amber-600' },
              {
                label: 'Total Paid',
                value: `₹${history.filter(h => h.status === 'COMPLETED').reduce((s, h) => s + h.amount, 0).toLocaleString('en-IN')}`,
                color: 'text-[#2ab391]'
              },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white rounded-xl border border-gray-200/90 p-3 sm:p-4 shadow-xs text-center">
                <p className="text-[10px] sm:text-xs text-gray-400 mb-0.5 sm:mb-1 font-semibold uppercase tracking-wider">{label}</p>
                <p className={`text-lg sm:text-2xl font-bold ${color || 'text-gray-900'}`}>{value}</p>
              </div>
            ))}
          </div>

          {/* Payments Table (Scrollable on Mobile & Desktop) */}
          <div className="overflow-x-auto rounded-2xl border border-gray-200/90 shadow-xs bg-white">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-200/80">
                  <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">#</th>
                  <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Date &amp; Time</th>
                  <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Type</th>
                  <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Edition</th>
                  <th className="text-center px-3 sm:px-4 py-2.5 sm:py-3 text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Qty</th>
                  <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Amount</th>
                  <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                  <th className="text-left px-3 sm:px-4 py-2.5 sm:py-3 text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider min-w-[190px]">Order ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.map((item, idx) => (
                  <tr key={`${item.type}-${item.id}`} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-gray-400 text-[11px] sm:text-xs font-medium whitespace-nowrap">{idx + 1}</td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5 whitespace-nowrap">
                      <p className="text-gray-900 font-semibold text-xs sm:text-sm">{formatDate(item.created_at)}</p>
                      <p className="text-gray-400 text-[10px] sm:text-[11px] font-medium">{formatTime(item.created_at)}</p>
                    </td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-[11px] sm:text-xs font-semibold ${
                        item.type === 'Yearly Subscription'
                          ? 'bg-[#e0f7f8] text-[#2ab391] border border-[#2ab391]/20'
                          : 'bg-indigo-50 text-indigo-600 border border-indigo-200/50'
                      }`}>
                        {item.type}
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-gray-700 font-medium text-xs sm:text-sm whitespace-nowrap">{item.edition || '—'}</td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-gray-700 font-medium text-xs sm:text-sm text-center whitespace-nowrap">{item.quantity ?? '—'}</td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5 font-bold text-[#2ab391] text-xs sm:text-sm whitespace-nowrap">
                      ₹{item.amount?.toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5 whitespace-nowrap">{getStatusBadge(item.status)}</td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-xs text-gray-700 font-medium select-all break-all min-w-[190px]" title={item.razorpay_order_id}>
                      {item.razorpay_order_id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentHistoryPage;
